/**
 * 消息和通知API函数
 * 提供系统消息和用户通知相关功能
 */
import { supabase } from '../supabaseClient';

/**
 * 获取消息列表
 * @param {Object} options - 查询选项
 * @returns {Promise<Array>} 消息列表
 */
export const getUserMessages = async (options = {}) => {
  try {
    let query = supabase
      .from('messages')
      .select('*')
      .order('created_at', { ascending: false });
    
    // 应用查询选项
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    if (options.onlyUnread === true) {
      query = query.eq('is_read', false);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('获取消息出错:', error);
    return [];
  }
};

/**
 * 创建新消息
 * @param {Object} messageData - 消息数据
 * @returns {Promise<Object|null>} 创建的消息或null
 */
export const createMessage = async (messageData) => {
  try {
    // 确保有创建时间和默认的读取状态
    const message = {
      ...messageData,
      created_at: messageData.created_at || new Date().toISOString(),
      is_read: messageData.is_read ?? false
    };
    
    const { data, error } = await supabase
      .from('messages')
      .insert([message])
      .select()
      .single();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('创建消息出错:', error);
    return null;
  }
};

/**
 * 将消息标记为已读
 * @param {number} messageId - 消息ID
 * @returns {Promise<boolean>} 操作是否成功
 */
export const markMessageAsRead = async (messageId) => {
  try {
    if (!messageId) throw new Error('消息ID不能为空');
    
    const { error } = await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('id', messageId);
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('标记消息为已读出错:', error);
    return false;
  }
};

/**
 * 将所有消息标记为已读
 * @returns {Promise<Object>} 操作结果
 */
export const markAllMessagesAsRead = async () => {
  try {
    const { data, error } = await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('is_read', false);
      
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('标记所有消息为已读出错:', error);
    return { success: false, error };
  }
};

/**
 * 删除消息
 * @param {number} messageId - 消息ID
 * @returns {Promise<boolean>} 操作是否成功
 */
export const deleteMessage = async (messageId) => {
  try {
    if (!messageId) throw new Error('消息ID不能为空');
    
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId);
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('删除消息出错:', error);
    return false;
  }
};

/**
 * 获取未读消息数量
 * @returns {Promise<number>} 未读消息数量
 */
export const getUnreadMessagesCount = async () => {
  try {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('is_read', false);
      
    if (error) throw error;
    
    return count || 0;
  } catch (error) {
    console.error('获取未读消息数量出错:', error);
    return 0;
  }
}; 